<script setup>
/* eslint semi: 1 */
import { onMounted } from 'vue';
import { useForm } from 'vee-validate';
import { toTypedSchema } from '@vee-validate/yup';
import * as yup from 'yup';
import {
  resetPassword as AmplifyResetPassword,
  confirmResetPassword as AmplifyConfirmResetPassword
} from 'aws-amplify/auth';

import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth';
import { t } from '@/i18n/i18ns';
import CONSTANTS from '@/globals/constants';

// Component imports
import FormInput from '@/components/common/form/form-input-tmpl.vue';
import FormButton from '@/components/common/form/form-button-tmpl.vue';

// Mixin imports
import AuthMixin from '@/components/mixins/auth-mixin';
import EventBus from '@/components/mixins/event-bus-mixin';

const cognitoAuthStore = useCognitoAuthStore();

defineOptions({
  mixins: [AuthMixin]
});

const emit = defineEmits(['successSubmit', 'failedReset']);

const props = defineProps({
  isPageMini: {
    type: Boolean,
    default: false,
  },
  email: {
    type: String,
    default: null,
  },
  isAutoVerifyEmail: {
    type: Boolean,
    default: false,
  }
});

const schema = yup.object({
  verificationCode: yup.string()
    .transform((value) => value?.trim())
    .required('label.codeIsRequired'),
  newPassword: yup.string()
    .required('label.requiredPassword')
    .matches(CONSTANTS.PASSWORD_REGEX, 'label.passwordHint'),
  confirmNewPassword: yup.string()
    .required('label.requiredConfirmPassword')
    .matches(CONSTANTS.PASSWORD_REGEX, 'label.passwordHint')
});

const {
  errors,
  handleSubmit,
  isSubmitting,
  defineField,
  setFieldError,
  setErrors
} = useForm({
  validationSchema: toTypedSchema(schema),
});

const [verificationCode] = defineField('verificationCode');
const [newPassword] = defineField('newPassword');
const [confirmNewPassword] = defineField('confirmNewPassword');

const performForgotPassword = async (username) => {
  try {
    await cognitoAuthStore.configureWithEmail(username);
    await AmplifyResetPassword({ username });
  } catch (error) {
    console.error('Error performing forgot password:', error);
    return { error, success: false };
  }

  return { error: null, success: true };
};

const performChangePassword = async (code, password) => {
  const username = (props.email || '').toLowerCase();

  try {
    await cognitoAuthStore.configureWithEmail(username);
    await AmplifyConfirmResetPassword({
      username,
      confirmationCode: code,
      newPassword: password
    });
  } catch (error) {
    console.error('Error performing change password:', error);
    return { error, success: false };
  }

  return { error: null, success: true };
};

const onSubmit = handleSubmit(async (values, actions) => {
  // Handle form submission
  // Check if newPassword and confirmNewPassword is the same
  if (values.newPassword !== values.confirmNewPassword) {
    actions.setFieldError('confirmNewPassword', 'label.passwordDidNotMatch');
    return;
  }

  const { error } = await performChangePassword(values.verificationCode, values.newPassword);
  if (error) {
    if ([CONSTANTS.EXPIRED_CODE_EXCEPTION, CONSTANTS.CODE_MISMATCH_EXCEPTION].includes(error.name)) {
      setFieldError('verificationCode', 'errors.invalid.verificationCode');
    } else if ([CONSTANTS.INVALID_PASSWORD, CONSTANTS.INVALID_PARAMETER_EXCEPTION].includes(error.name)) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('label.passwordPolicyRequirementText')
        },
        callbacks: {
          eventOnClose: () => {}
        }
      });

      setFieldError('newPassword', 'label.passwordHint');
    } else if (error.message.includes('Attempt limit exceeded')) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('label.passwordAttemptLimitExceeded')
        },
        callbacks: {
          eventOnClose: () => {}
        }
      });
    } else {
      setErrors({
        verificationCode: ' ',
        newPassword: ' ',
        confirmNewPassword: error.message
      });
    }

    return;
  }

  emit('successSubmit', values);
});

onMounted(async () => {
  if (props.isAutoVerifyEmail && props.email) {
    // Automatically verify the account if isAutoVerifyEmail is true
    isSubmitting.value = true;
    const result = await performForgotPassword(props.email);
    if (result.error) {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('label.passwordAttemptLimitExceeded')
        },
        callbacks: {
          eventOnClose: () => { emit('failedReset'); }
        }
      });
    }
    isSubmitting.value = false;
  }
});
</script>

<template>
  <form
    class="max-[1919px]:my-3 min-[1920px]:my-5 flex flex-col w-full h-full"
    data-component-element-id="forgot-password-reset-password-form"
    @submit="onSubmit"
  >
    <div class="flex flex-col flex-1 max-[1919px]:gap-4 min-[1920px]:gap-6">
      <div class="max-[1919px]:py-2 min-[1920px]:py-4 max-[1919px]:text-xs min-[1920px]:text-md text-white text-center">
        <span>{{ $t('label.verificationSentToEmailIfExist') }}</span>
      </div>

      <form-input
        id="verification-code"
        v-model="verificationCode"
        type="text"
        inputTheme="light"
        required
        :label="$t('label.verificationCode')"
        :size="isPageMini ? 'xs' : 'md'"
        :disabled="isSubmitting"
        :validationStatus="errors.verificationCode ? 'error' : undefined"
        data-component-element-id="forgot-password-verification-code-input"
      >
        <template
          v-if="errors?.verificationCode"
          #validationTransparent
        >
          {{ $t(errors.verificationCode) }}
        </template>
      </form-input>

      <form-input
        id="new-password"
        v-model="newPassword"
        type="password"
        inputTheme="light"
        required
        :label="$t('label.newPassword')"
        :size="isPageMini ? 'xs' : 'md'"
        :disabled="isSubmitting"
        :validationStatus="errors.newPassword ? 'error' : undefined"
        data-component-element-id="forgot-password-new-password-input"
      >
        <template
          v-if="errors?.newPassword"
          #validationTransparent
        >
          {{ $t(errors.newPassword) }}
        </template>
      </form-input>

      <form-input
        id="confirmNewPassword"
        v-model="confirmNewPassword"
        type="password"
        inputTheme="light"
        required
        :label="$t('label.confirmNewPassword')"
        :size="isPageMini ? 'xs' : 'md'"
        :disabled="isSubmitting"
        :validationStatus="errors.confirmNewPassword ? 'error' : undefined"
        data-component-element-id="forgot-password-confirm-new-password-input"
      >
        <template
          v-if="errors?.confirmNewPassword"
          #validationTransparent
        >
          {{ $t(errors.confirmNewPassword) }}
        </template>
      </form-input>
    </div>

    <form-button
      type="submit"
      buttonTheme="light"
      data-component-element-id="forgot-password-reset-password-submit"
      :size="isPageMini ? 'xs' : 'md'"
      :loading="isSubmitting"
      :disabled="isSubmitting"
      :label="$t('label.resetPassword')"
      @click="onSubmit"
    />
  </form>
</template>
